import Check from "../Core/Check.js";
import Frozen from "../Core/Frozen.js";
import defined from "../Core/defined.js";
import DeveloperError from "../Core/DeveloperError.js";
import getAbsoluteUri from "../Core/getAbsoluteUri.js";
import GltfLoaderUtil from "./GltfLoaderUtil.js";
import hasExtension from "./hasExtension.js";

/**
 * Compute cache keys for resources in {@link ResourceCache}.
 *
 * @namespace ResourceCacheKey
 *
 * @private
 */
const ResourceCacheKey = {};
function getExternalResourceCacheKey(resource) {
  return getAbsoluteUri(resource.url);
}
function getBufferViewCacheKey(bufferView) {
  let {
    byteOffset,
    byteLength
  } = bufferView;
  if (hasExtension(bufferView, "EXT_meshopt_compression")) {
    var _meshopt$byteOffset;
    const meshopt = bufferView.extensions.EXT_meshopt_compression;
    byteOffset = (_meshopt$byteOffset = meshopt.byteOffset) !== null && _meshopt$byteOffset !== void 0 ? _meshopt$byteOffset : 0;
    byteLength = meshopt.byteLength;
  }
  return "".concat(byteOffset, "-").concat(byteOffset + byteLength);
}
function getAccessorCacheKey(accessor, bufferView) {
  const byteOffset = bufferView.byteOffset + accessor.byteOffset;
  const {
    componentType,
    type,
    count
  } = accessor;
  return "".concat(byteOffset, "-").concat(componentType, "-").concat(type, "-").concat(count);
}
function getEmbeddedBufferCacheKey(parentResource, bufferId) {
  const parentCacheKey = getExternalResourceCacheKey(parentResource);
  return "".concat(parentCacheKey, "-buffer-id-").concat(bufferId);
}
function getBufferCacheKey(buffer, bufferId, gltfResource, baseResource) {
  if (defined(buffer.uri)) {
    const resource = baseResource.getDerivedResource({
      url: buffer.uri
    });
    return getExternalResourceCacheKey(resource);
  }
  return getEmbeddedBufferCacheKey(gltfResource, bufferId);
}
function getDracoCacheKey(gltf, draco, gltfResource, baseResource) {
  const bufferViewId = draco.bufferView;
  const bufferView = gltf.bufferViews[bufferViewId];
  const bufferId = bufferView.buffer;
  const buffer = gltf.buffers[bufferId];
  const bufferCacheKey = getBufferCacheKey(buffer, bufferId, gltfResource, baseResource);
  const bufferViewCacheKey = getBufferViewCacheKey(bufferView);
  return "".concat(bufferCacheKey, "-range-").concat(bufferViewCacheKey);
}
function getSpzCacheKey(gltf, primitive, gltfResource, baseResource) {
  const bufferViewId = 0;
  const bufferView = gltf.bufferViews[bufferViewId];
  const bufferId = bufferView.buffer;
  const buffer = gltf.buffers[bufferId];
  const bufferCacheKey = getBufferCacheKey(buffer, bufferId, gltfResource, baseResource);
  const bufferViewCacheKey = getBufferViewCacheKey(bufferView);
  return "".concat(bufferCacheKey, "-range-").concat(bufferViewCacheKey);
}
function getImageCacheKey(gltf, imageId, gltfResource, baseResource) {
  const image = gltf.images[imageId];
  const bufferViewId = image.bufferView;
  const uri = image.uri;
  if (defined(uri)) {
    const resource = baseResource.getDerivedResource({
      url: uri
    });
    return getExternalResourceCacheKey(resource);
  }
  const bufferView = gltf.bufferViews[bufferViewId];
  const bufferId = bufferView.buffer;
  const buffer = gltf.buffers[bufferId];
  const bufferCacheKey = getBufferCacheKey(buffer, bufferId, gltfResource, baseResource);
  const bufferViewCacheKey = getBufferViewCacheKey(bufferView);
  return "".concat(bufferCacheKey, "-range-").concat(bufferViewCacheKey);
}
function getSamplerCacheKey(gltf, textureInfo) {
  const sampler = GltfLoaderUtil.createSampler({
    gltf: gltf,
    textureInfo: textureInfo
  });
  return "".concat(sampler.wrapS, "-").concat(sampler.wrapT, "-").concat(sampler.minificationFilter, "-").concat(sampler.magnificationFilter);
}

/**
 * Gets the schema cache key.
 *
 * @param {object} options Object with the following properties:
 * @param {object} [options.schema] An object that explicitly defines a schema JSON. Mutually exclusive with options.resource.
 * @param {Resource} [options.resource] The {@link Resource} pointing to the schema JSON. Mutually exclusive with options.schema.
 *
 * @returns {string} The schema cache key.
 *
 * @exception {DeveloperError} One of options.schema and options.resource must be defined.
 * @private
 */
ResourceCacheKey.getSchemaCacheKey = function (options) {
  const {
    schema,
    resource
  } = options;
  if (defined(schema)) {
    return "embedded-schema:".concat(JSON.stringify(schema));
  }
  return "external-schema:".concat(getExternalResourceCacheKey(resource));
};

/**
 * Gets the external buffer cache key.
 *
 * @param {object} options Object with the following properties:
 * @param {Resource} options.resource The {@link Resource} pointing to the external buffer.
 *
 * @returns {string} The external buffer cache key.
 * @private
 */
ResourceCacheKey.getExternalBufferCacheKey = function (options) {
  options = options !== null && options !== void 0 ? options : Frozen.EMPTY_OBJECT;
  const {
    resource
  } = options;
  return "external-buffer:".concat(getExternalResourceCacheKey(resource));
};

/**
 * Gets the embedded buffer cache key.
 *
 * @param {object} options Object with the following properties:
 * @param {Resource} options.parentResource The {@link Resource} containing the embedded buffer.
 * @param {number} options.bufferId A unique identifier of the embedded buffer within the parent resource.
 *
 * @returns {string} The embedded buffer cache key.
 * @private
 */
ResourceCacheKey.getEmbeddedBufferCacheKey = function (options) {
  options = options !== null && options !== void 0 ? options : Frozen.EMPTY_OBJECT;
  const {
    parentResource,
    bufferId
  } = options;
  return "embedded-buffer:".concat(getEmbeddedBufferCacheKey(parentResource, bufferId));
};

/**
 * Gets the glTF cache key.
 *
 * @param {object} options Object with the following properties:
 * @param {Resource} options.gltfResource The {@link Resource} containing the glTF.
 *
 * @returns {string} The glTF cache key.
 * @private
 */
ResourceCacheKey.getGltfCacheKey = function (options) {
  options = options !== null && options !== void 0 ? options : Frozen.EMPTY_OBJECT;
  const {
    gltfResource
  } = options;
  return "gltf:".concat(getExternalResourceCacheKey(gltfResource));
};

/**
 * Gets the buffer view cache key.
 *
 * @param {object} options Object with the following properties:
 * @param {object} options.gltf The glTF JSON.
 * @param {number} options.bufferViewId The bufferView ID.
 * @param {Resource} options.gltfResource The {@link Resource} containing the glTF.
 * @param {Resource} options.baseResource The {@link Resource} that paths in the glTF JSON are relative to.
 *
 * @returns {string} The buffer view cache key.
 * @private
 */
ResourceCacheKey.getBufferViewCacheKey = function (options) {
  options = options !== null && options !== void 0 ? options : Frozen.EMPTY_OBJECT;
  const {
    gltf,
    bufferViewId,
    gltfResource,
    baseResource
  } = options;
  const bufferView = gltf.bufferViews[bufferViewId];
  let bufferId = bufferView.buffer;
  const buffer = gltf.buffers[bufferId];
  if (hasExtension(bufferView, "EXT_meshopt_compression")) {
    const meshopt = bufferView.extensions.EXT_meshopt_compression;
    bufferId = meshopt.buffer;
  }
  const bufferCacheKey = getBufferCacheKey(buffer, bufferId, gltfResource, baseResource);
  const bufferViewCacheKey = getBufferViewCacheKey(bufferView);
  return "buffer-view:".concat(bufferCacheKey, "-range-").concat(bufferViewCacheKey);
};

/**
 * Gets the Draco cache key.
 *
 * @param {object} options Object with the following properties:
 * @param {object} options.gltf The glTF JSON.
 * @param {object} options.draco The Draco extension object.
 * @param {Resource} options.gltfResource The {@link Resource} containing the glTF.
 * @param {Resource} options.baseResource The {@link Resource} that paths in the glTF JSON are relative to.
 *
 * @returns {string} The Draco cache key.
 * @private
 */
ResourceCacheKey.getDracoCacheKey = function (options) {
  options = options !== null && options !== void 0 ? options : Frozen.EMPTY_OBJECT;
  const {
    gltf,
    draco,
    gltfResource,
    baseResource
  } = options;
  return "draco:".concat(getDracoCacheKey(gltf, draco, gltfResource, baseResource));
};
ResourceCacheKey.getSpzCacheKey = function (options) {
  options = options !== null && options !== void 0 ? options : Frozen.EMPTY_OBJECT;
  const {
    gltf,
    primitive,
    gltfResource,
    baseResource
  } = options;
  return "spz:".concat(getSpzCacheKey(gltf, primitive, gltfResource, baseResource));
};
/**
 * Gets the vertex buffer cache key.
 *
 * @param {object} options Object with the following properties:
 * @param {object} options.gltf The glTF JSON.
 * @param {Resource} options.gltfResource The {@link Resource} containing the glTF.
 * @param {Resource} options.baseResource The {@link Resource} that paths in the glTF JSON are relative to.
 * @param {FrameState} options.frameState The frame state.
 * @param {number} [options.bufferViewId] The bufferView ID corresponding to the vertex buffer.
 * @param {object} [options.draco] The Draco extension object.
 * @param {string} [options.attributeSemantic] The attribute semantic, e.g. POSITION or NORMAL.
 * @param {boolean} [options.dequantize=false] Determines whether or not the vertex buffer will be dequantized on the CPU.
 * @param {boolean} [options.loadBuffer=false] Load vertex buffer as a GPU vertex buffer.
 * @param {boolean} [options.loadTypedArray=false] Load vertex buffer as a typed array.
 * @exception {DeveloperError} One of options.bufferViewId and options.draco must be defined.
 * @exception {DeveloperError} When options.draco is defined options.attributeSemantic must also be defined.
 *
 * @returns {string} The vertex buffer cache key.
 * @private
 */
ResourceCacheKey.getVertexBufferCacheKey = function (options) {
  options = options !== null && options !== void 0 ? options : Frozen.EMPTY_OBJECT;
  const {
    gltf,
    gltfResource,
    baseResource,
    frameState,
    bufferViewId,
    draco,
    spz,
    attributeSemantic,
    dequantize = false,
    loadBuffer = false,
    loadTypedArray = false
  } = options;
  let cacheKeySuffix = "";
  if (dequantize) {
    cacheKeySuffix += "-dequantize";
  }
  if (loadBuffer) {
    cacheKeySuffix += "-buffer";
    cacheKeySuffix += "-context-".concat(frameState.context.id);
  }
  if (loadTypedArray) {
    cacheKeySuffix += "-typed-array";
  }
  if (defined(draco)) {
    const dracoCacheKey = getDracoCacheKey(gltf, draco, gltfResource, baseResource);
    return "vertex-buffer:".concat(dracoCacheKey, "-draco-").concat(attributeSemantic).concat(cacheKeySuffix);
  }
  if (spz) {
    const spzCacheKey = getSpzCacheKey(gltf, spz, gltfResource, baseResource);
    return "vertex-buffer:".concat(spzCacheKey, "-spz-").concat(attributeSemantic).concat(cacheKeySuffix);
  }
  const bufferView = gltf.bufferViews[bufferViewId];
  const bufferId = bufferView.buffer;
  const buffer = gltf.buffers[bufferId];
  const bufferCacheKey = getBufferCacheKey(buffer, bufferId, gltfResource, baseResource);
  const bufferViewCacheKey = getBufferViewCacheKey(bufferView);
  return "vertex-buffer:".concat(bufferCacheKey, "-range-").concat(bufferViewCacheKey).concat(cacheKeySuffix);
};
function hasDracoCompression(draco, semantic) {
  return defined(draco) && defined(draco.attributes) && defined(draco.attributes[semantic]);
}

/**
 * Gets the index buffer cache key.
 *
 * @param {object} options Object with the following properties:
 * @param {object} options.gltf The glTF JSON.
 * @param {number} options.accessorId The accessor ID corresponding to the index buffer.
 * @param {Resource} options.gltfResource The {@link Resource} containing the glTF.
 * @param {Resource} options.baseResource The {@link Resource} that paths in the glTF JSON are relative to.
 * @param {FrameState} options.frameState The frame state.
 * @param {object} [options.draco] The Draco extension object.
 * @param {boolean} [options.loadBuffer=false] Load index buffer as a GPU index buffer.
 * @param {boolean} [options.loadTypedArray=false] Load index buffer as a typed array.
 *
 * @returns {string} The index buffer cache key.
 * @private
 */
ResourceCacheKey.getIndexBufferCacheKey = function (options) {
  options = options !== null && options !== void 0 ? options : Frozen.EMPTY_OBJECT;
  const {
    gltf,
    accessorId,
    gltfResource,
    baseResource,
    frameState,
    draco,
    loadBuffer = false,
    loadTypedArray = false
  } = options;
  let cacheKeySuffix = "";
  if (loadBuffer) {
    cacheKeySuffix += "-buffer";
    cacheKeySuffix += "-context-".concat(frameState.context.id);
  }
  if (loadTypedArray) {
    cacheKeySuffix += "-typed-array";
  }
  if (defined(draco)) {
    const dracoCacheKey = getDracoCacheKey(gltf, draco, gltfResource, baseResource);
    return "index-buffer:".concat(dracoCacheKey, "-draco").concat(cacheKeySuffix);
  }
  const accessor = gltf.accessors[accessorId];
  const bufferViewId = accessor.bufferView;
  const bufferView = gltf.bufferViews[bufferViewId];
  const bufferId = bufferView.buffer;
  const buffer = gltf.buffers[bufferId];
  const bufferCacheKey = getBufferCacheKey(buffer, bufferId, gltfResource, baseResource);
  const accessorCacheKey = getAccessorCacheKey(accessor, bufferView);
  return "index-buffer:".concat(bufferCacheKey, "-accessor-").concat(accessorCacheKey).concat(cacheKeySuffix);
};

/**
 * Gets the image cache key.
 *
 * @param {object} options Object with the following properties:
 * @param {object} options.gltf The glTF JSON.
 * @param {number} options.imageId The image ID.
 * @param {Resource} options.gltfResource The {@link Resource} containing the glTF.
 * @param {Resource} options.baseResource The {@link Resource} that paths in the glTF JSON are relative to.
 *
 * @returns {string} The image cache key.
 * @private
 */
ResourceCacheKey.getImageCacheKey = function (options) {
  options = options !== null && options !== void 0 ? options : Frozen.EMPTY_OBJECT;
  const {
    gltf,
    imageId,
    gltfResource,
    baseResource
  } = options;
  const imageCacheKey = getImageCacheKey(gltf, imageId, gltfResource, baseResource);
  return "image:".concat(imageCacheKey);
};

/**
 * Gets the texture cache key.
 *
 * @param {object} options Object with the following properties:
 * @param {object} options.gltf The glTF JSON.
 * @param {object} options.textureInfo The texture info object.
 * @param {Resource} options.gltfResource The {@link Resource} containing the glTF.
 * @param {Resource} options.baseResource The {@link Resource} that paths in the glTF JSON are relative to.
 * @param {SupportedImageFormats} options.supportedImageFormats The supported image formats.
 * @param {FrameState} options.frameState The frame state.
 *
 * @returns {string} The texture cache key.
 * @private
 */
ResourceCacheKey.getTextureCacheKey = function (options) {
  options = options !== null && options !== void 0 ? options : Frozen.EMPTY_OBJECT;
  const {
    gltf,
    textureInfo,
    gltfResource,
    baseResource,
    supportedImageFormats,
    frameState
  } = options;
  const textureId = textureInfo.index;
  const imageId = GltfLoaderUtil.getImageIdFromTexture({
    gltf: gltf,
    textureId: textureId,
    supportedImageFormats: supportedImageFormats
  });
  const imageCacheKey = getImageCacheKey(gltf, imageId, gltfResource, baseResource);

  // Include the sampler cache key in the texture cache key since textures and
  // samplers are coupled in WebGL 1. When upgrading to WebGL 2 consider
  // removing the sampleCacheKey here.
  const samplerCacheKey = getSamplerCacheKey(gltf, textureInfo);
  return "texture:".concat(imageCacheKey, "-sampler-").concat(samplerCacheKey, "-context-").concat(frameState.context.id);
};
export default ResourceCacheKey;